/**
 * Append `ajs_aid` (the iframe's RudderStack anonymous id) to Allstar-owned
 * sponsor CTA links so a click-through can be identity-stitched on
 * allstar.gg — the same mechanism HLTVEmbeddablePlaylist uses on clip and
 * profile links. Without it, a viewer who clicks the Allstar self-takeover
 * branding and later signs up is only joinable via UTM aggregates, not as
 * an individual.
 *
 * STRICTLY scoped to allstar.gg hosts: sponsor campaigns' CTAs point at the
 * brand's own site, and appending our analytics id there would leak a
 * cross-site identifier to third parties. Non-allstar.gg URLs pass through
 * untouched.
 *
 * Also fills in utm_source (never overwriting one that's present) on
 * Allstar-owned CTAs, so a campaign configured without UTMs still lands
 * with a source.
 *
 * Best-effort by design: if RudderStack hasn't initialized yet, links get
 * the utm_source fill but no ajs_aid; malformed URLs pass through untouched.
 */
import type { ResolvedSponsorConfig } from "./@types";

const isAllstarHost = (hostname: string): boolean =>
  hostname === "allstar.gg" || hostname.endsWith(".allstar.gg");

// The utm_source the branded iframe's own configs already use (see
// sponsors.ts). Only filled in when a campaign's CTA has no utm_source of
// its own — existing values are never rewritten.
const DEFAULT_UTM_SOURCE = "hltv-iframe";

const decorateCtaUrl = (
  url: string | undefined,
  aid: string | undefined,
): string | undefined => {
  if (!url) return url;
  try {
    const parsed = new URL(url);
    if (!isAllstarHost(parsed.hostname)) return url;
    if (!parsed.searchParams.get("utm_source")) {
      parsed.searchParams.set("utm_source", DEFAULT_UTM_SOURCE);
    }
    if (aid) parsed.searchParams.set("ajs_aid", aid);
    return parsed.toString();
  } catch {
    // Relative/malformed URL — leave it alone.
    return url;
  }
};

export const decorateSponsorLinks = (
  config: ResolvedSponsorConfig,
): ResolvedSponsorConfig => {
  const aid = window.rudderanalytics?.getAnonymousId?.();
  const slots = Object.fromEntries(
    Object.entries(config.slots).map(([slot, creative]) => [
      slot,
      creative && typeof creative === "object"
        ? { ...creative, ctaUrl: decorateCtaUrl(creative.ctaUrl, aid) }
        : creative,
    ]),
  ) as ResolvedSponsorConfig["slots"];
  return { ...config, slots };
};
